import { useCallback, useEffect, useRef, useState } from "react";

import { Channel } from "pusher-js";
import { toast } from "react-toastify";
import { useSocket } from "components";
import { useReportsActions, useReportsQueriesSelector } from "store";
import {
  CanvasView,
  CustomConfig,
  DataTableCardData,
  PageMeta,
  ReportGeneratorSavedCard,
  ReportGeneratorTemplateListItem,
  ReportGeneratorTemplateSavePayload,
  ReportViewType
} from "store/types";

interface UseTemplateActionsProps {
  pages: CanvasView[][];
  pageMeta: PageMeta;
  reportTitle: string;
  logoUrl: string | null;
  logoFile: File | null;
  backgroundFile: File | null;
  backgroundUrl: string | null; // Unsplash URL (not local file)
  setPages: React.Dispatch<React.SetStateAction<CanvasView[][]>>;
  setPageMeta: React.Dispatch<React.SetStateAction<PageMeta>>;
  setReportTitle: (title: string) => void;
  setLogoUrl: (url: string | null) => void;
  setBackgroundUrl: (url: string | null) => void;
  setCurrentPageIndex: (index: number) => void;
}

interface UseTemplateActionsReturn {
  templates: ReportGeneratorTemplateListItem[];
  loadingTemplates: boolean;
  savingTemplate: boolean;
  loadingTemplate: boolean;
  fetchTemplates: () => Promise<void>;
  saveTemplate: (name: string, description?: string) => Promise<void>;
  updateTemplate: (id: number, name: string, description?: string) => Promise<void>;
  loadTemplate: (id: number) => Promise<void>;
  deleteTemplate: (id: number) => Promise<void>;
  currentTemplateId: number | null;
  setCurrentTemplateId: (id: number | null) => void;
}

export const useTemplateHook = ({
  pages,
  pageMeta,
  reportTitle,
  logoUrl,
  logoFile,
  backgroundFile,
  backgroundUrl,
  setPages,
  setPageMeta,
  setReportTitle,
  setLogoUrl,
  setBackgroundUrl,
  setCurrentPageIndex
}: UseTemplateActionsProps): UseTemplateActionsReturn => {
  const {
    getReportTemplatesAction,
    getReportTemplateAction,
    saveReportTemplateAction,
    updateReportTemplateAction,
    deleteReportTemplateAction,
    getBulkPieChartsAction,
    getReportTableAction,
    getCubicAction,
    getMeasureSummaryAction
  } = useReportsActions();

  const { dateRange, currency, exchangeMethod, filters, use_accounting_date } = useReportsQueriesSelector();

  const [templates, setTemplates] = useState<ReportGeneratorTemplateListItem[]>([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [savingTemplate, setSavingTemplate] = useState(false);
  const [loadingTemplate, setLoadingTemplate] = useState(false);
  const [currentTemplateId, setCurrentTemplateId] = useState<number | null>(null);

  // Track original loaded template state to detect changes on update
  const [loadedLogoUrl, setLoadedLogoUrl] = useState<string | null>(null);
  const [loadedBackgroundUrl, setLoadedBackgroundUrl] = useState<string | null>(null);

  // Subscribe to Pusher channel for OPSWAT file scanning results
  const socket = useSocket();
  const channelRef = useRef<Channel | null>(null);

  useEffect(() => {
    // Cleanup previous subscription
    if (channelRef.current) {
      channelRef.current.unbind_all();
      socket.unsubscribe(channelRef.current.name);
      channelRef.current = null;
    }

    if (!currentTemplateId) return;

    const channelName = `private-report-template-${currentTemplateId}`;
    const channel = socket.subscribe(channelName);
    channelRef.current = channel;

    channel.bind("file.checked", (data: { type: "logo" | "background"; file_url: string }) => {
      if (data.type === "logo" && data.file_url) {
        setLogoUrl(data.file_url);
        setLoadedLogoUrl(data.file_url);
      } else if (data.type === "background" && data.file_url) {
        setBackgroundUrl(data.file_url);
        setLoadedBackgroundUrl(data.file_url);
      }
    });

    return () => {
      channel.unbind_all();
      socket.unsubscribe(channelName);
      channelRef.current = null;
    };
  }, [currentTemplateId, socket, setLogoUrl, setBackgroundUrl]);

  // Fetch data for a single card based on its type and config (API payload format)
  const fetchCardData = useCallback(
    async (card: CanvasView): Promise<CanvasView> => {
      const config = card.customConfig;
      if (!config) return card;

      try {
        switch (card.type) {
          case "chart": {
            if (!config.graphDataFormation) return card;
            const chartKey = Object.keys(config.graphDataFormation)[0];
            const response = await getBulkPieChartsAction({
              filters,
              graphDataFormation: config.graphDataFormation,
              dateRange,
              currency,
              exchangeMethod,
              use_accounting_date
            });
            return { ...card, data: response?.[chartKey] };
          }

          case "data_table": {
            if (!config.columns?.length) return card;
            const response = await getReportTableAction({
              dateRange,
              currency,
              exchangeMethod,
              filters,
              measures: config.measures || [],
              calculatedFields: config.calculatedFields || [],
              characteristics: config.characteristics || [],
              columns: config.columns,
              groupBy: config.groupBy || [],
              columnBy: config.columnBy || "measure",
              rowBy: config.rowBy || "deal",
              originalCurrency: false,
              use_accounting_date,
              disableDispatch: true
            });

            const apiColumns = response?.tableData?.columns || [];
            return {
              ...card,
              data: {
                columns: [
                  { key: "name", title: "Name" },
                  ...apiColumns.map((col) => ({ key: col.key, title: col.title }))
                ],
                data: (response?.tableData?.rows || []) as DataTableCardData["data"]
              }
            };
          }

          case "kpi_card": {
            if (!config.cubics) return card;
            const response = await getCubicAction({
              cubics: config.cubics,
              pageType: "document",
              filters,
              dateRange,
              currency,
              exchangeMethod,
              use_accounting_date
            });
            return { ...card, data: response };
          }

          case "summary_card": {
            if (!config.parameter) return card;
            const response = await getMeasureSummaryAction({
              parameter: config.parameter,
              filters,
              dateRange,
              currency,
              exchangeMethod,
              use_accounting_date
            });
            return { ...card, data: response };
          }

          case "ai_card": {
            // AI cards store answer in customConfig, no need to refetch
            if (config.answer) {
              return { ...card, data: { answer: config.answer } };
            }
            return card;
          }

          case "text_card": {
            // Text cards don't need data fetching
            if (config.content) {
              return { ...card, data: { content: config.content } };
            }
            return card;
          }

          default:
            return card;
        }
      } catch (error) {
        console.error(`Failed to fetch data for ${card.type} card:`, error);
        return card;
      }
    },
    [
      filters,
      dateRange,
      currency,
      exchangeMethod,
      use_accounting_date,
      getBulkPieChartsAction,
      getReportTableAction,
      getCubicAction,
      getMeasureSummaryAction
    ]
  );

  // Build save payload from current state
  const buildSavePayload = useCallback(
    (name: string, description?: string, isUpdate = false): ReportGeneratorTemplateSavePayload => {
      // Strip runtime data from cards, keep only layout + config
      const content = pages.map((page) =>
        page.map((card) => ({
          type: card.type,
          title: card.title,
          gridCol: card.gridCol,
          gridRow: card.gridRow,
          gridSpan: card.gridSpan,
          gridRowSpan: card.gridRowSpan,
          customConfig: card.customConfig as Record<string, unknown> | undefined
        }))
      );

      // Build content_metadata with only opacity (not URLs)
      const metadataForSave = {
        backgroundOpacity: pageMeta.backgroundOpacity,
        hasBackground: !!backgroundUrl
      };

      // Only include content_metadata if it has meaningful data
      const hasMetadata =
        metadataForSave.hasBackground || (metadataForSave.backgroundOpacity !== undefined && metadataForSave.backgroundOpacity !== 100);

      const payload: ReportGeneratorTemplateSavePayload = {
        name,
        content: JSON.stringify(content),
        ...(hasMetadata && { content_metadata: JSON.stringify(metadataForSave) })
      };

      if (description) payload.description = description;
      if (reportTitle) payload.report_title = reportTitle;

      if (isUpdate) {
        // Logo: only send if changed
        if (logoFile) {
          // New logo uploaded
          payload.logo = logoFile;
        } else if (loadedLogoUrl && !logoUrl) {
          // Logo was removed — send empty string
          payload.logo_url = "";
        }

        // Background: only send if changed
        if (backgroundFile) {
          payload.background = backgroundFile;
        } else if (backgroundUrl && backgroundUrl !== loadedBackgroundUrl) {
          payload.background_url = backgroundUrl;
        } else if (loadedBackgroundUrl && !backgroundUrl) {
          // Background was removed — send empty string
          payload.background_url = "";
        }
      } else {
        // Create: always send logo and background if present
        if (logoFile) payload.logo = logoFile;

        if (backgroundFile) {
          payload.background = backgroundFile;
        } else if (backgroundUrl) {
          payload.background_url = backgroundUrl;
        }
      }

      return payload;
    },
    [pages, pageMeta, reportTitle, logoUrl, logoFile, backgroundFile, backgroundUrl, loadedLogoUrl, loadedBackgroundUrl]
  );

  // Fetch all templates
  const fetchTemplates = useCallback(async () => {
    setLoadingTemplates(true);
    try {
      const data = await getReportTemplatesAction();
      setTemplates(data);
    } catch (error) {
      console.error("Failed to fetch templates:", error);
      toast.error("Failed to load templates");
    } finally {
      setLoadingTemplates(false);
    }
  }, [getReportTemplatesAction]);

  // Save new template
  const saveTemplate = useCallback(
    async (name: string, description?: string) => {
      setSavingTemplate(true);
      try {
        const payload = buildSavePayload(name, description);
        const data = await saveReportTemplateAction(payload);
        setCurrentTemplateId(data.id || null);
        toast.success("Template saved successfully");
      } catch (error) {
        console.error("Failed to save template:", error);
        toast.error("Failed to save template");
      } finally {
        setSavingTemplate(false);
      }
    },
    [buildSavePayload, saveReportTemplateAction]
  );

  // Update existing template
  const updateTemplate = useCallback(
    async (id: number, name: string, description?: string) => {
      setSavingTemplate(true);
      try {
        const payload = buildSavePayload(name, description, true);
        await updateReportTemplateAction(id, payload);
        toast.success("Template updated successfully");
        await fetchTemplates();
      } catch (error) {
        console.error("Failed to update template:", error);
        toast.error("Failed to update template");
      } finally {
        setSavingTemplate(false);
      }
    },
    [buildSavePayload, updateReportTemplateAction, fetchTemplates]
  );

  // Load template and hydrate state
  const loadTemplate = useCallback(
    async (id: number) => {
      setLoadingTemplate(true);
      try {
        const template = await getReportTemplateAction(id);

        // Set basic state
        setReportTitle(template.report_title || "");

        // Find logo file from files array
        const logoFile = template.files?.find((f) => f.type === "logo");
        const loadedLogo = logoFile?.file_url || null;
        setLogoUrl(loadedLogo);
        setLoadedLogoUrl(loadedLogo);

        // Find background file from files array
        const backgroundFile = template.files?.find((f) => f.type === "background");
        const loadedBackground = backgroundFile?.file_url || null;
        setBackgroundUrl(loadedBackground);
        setLoadedBackgroundUrl(loadedBackground);

        // Parse content and content_metadata (they come as JSON strings from API)
        const parsedContent = typeof template.content === "string"
          ? JSON.parse(template.content)
          : template.content;
        const parsedMetadata = typeof template.content_metadata === "string"
          ? JSON.parse(template.content_metadata)
          : template.content_metadata;

        // Reconstruct pageMeta (only opacity, URL is separate state)
        const meta = Array.isArray(parsedMetadata) ? parsedMetadata[0] : parsedMetadata;
        const reconstructedMeta: PageMeta = {
          backgroundOpacity: meta?.backgroundOpacity ?? 100
        };

        setPageMeta(reconstructedMeta);
        setCurrentTemplateId(template.id || null);

        // Convert saved cards to canvas cards (without data initially)
        const hydratedPages: CanvasView[][] = (parsedContent || []).map((page: ReportGeneratorSavedCard[], pageIndex: number) =>
          page.map((savedCard: ReportGeneratorSavedCard) => ({
            ...savedCard,
            type: savedCard.type as ReportViewType,
            customConfig: savedCard.customConfig as CustomConfig | undefined,
            id: `card-${Date.now()}-${Math.random().toString(36).substring(2, 11)}`,
            data: undefined,
            pageIndex
          }))
        );

        // Set pages first so UI shows the layout
        setPages(hydratedPages);
        setCurrentPageIndex(0);
        toast.success(`Template "${template.name}" loaded`);

        // Fetch data for all cards in parallel
        const pagesWithData = await Promise.all(
          hydratedPages.map(async (page) => {
            const cardsWithData = await Promise.all(page.map((card) => fetchCardData(card)));
            return cardsWithData;
          })
        );

        // Update pages with fetched data
        setPages(pagesWithData);
      } catch (error) {
        console.error("Failed to load template:", error);
        toast.error("Failed to load template");
      } finally {
        setLoadingTemplate(false);
      }
    },
    [getReportTemplateAction, setReportTitle, setLogoUrl, setBackgroundUrl, setPageMeta, setPages, setCurrentPageIndex, fetchCardData]
  );

  // Delete template
  const deleteTemplate = useCallback(
    async (id: number) => {
      try {
        await deleteReportTemplateAction(id);
        if (currentTemplateId === id) {
          // Reset to new report state
          setPages([[]]);
          setPageMeta({});
          setCurrentPageIndex(0);
          setReportTitle("Altius Report");
          setLogoUrl(null);
          setBackgroundUrl(null);
          setCurrentTemplateId(null);
          setLoadedLogoUrl(null);
          setLoadedBackgroundUrl(null);
        }
        toast.success("Template deleted");
        await fetchTemplates();
      } catch (error) {
        console.error("Failed to delete template:", error);
        toast.error("Failed to delete template");
      }
    },
    [deleteReportTemplateAction, currentTemplateId, fetchTemplates, setPages, setPageMeta, setCurrentPageIndex, setReportTitle, setLogoUrl, setBackgroundUrl]
  );

  return {
    templates,
    loadingTemplates,
    savingTemplate,
    loadingTemplate,
    fetchTemplates,
    saveTemplate,
    updateTemplate,
    loadTemplate,
    deleteTemplate,
    currentTemplateId,
    setCurrentTemplateId: (id: number | null) => {
      setCurrentTemplateId(id);
      if (!id) {
        setLoadedLogoUrl(null);
        setLoadedBackgroundUrl(null);
      }
    }
  };
};
